// Fiche d'une étude : son PROTOCOLE, lisible pendant la session, et son cadrage
// modifiable.
//
// Le protocole se lit comme un document de terrain — questions numérotées,
// relances en retrait — et se copie en texte brut pour l'imprimer ou le coller
// dans un document. Un test ou un audit renvoient vers leur fiche : c'est là
// que se notent les résultats.
import { useState, type FormEvent } from 'react';
import { useUpdateStudy } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { Study, StudyProtocol } from '../api/types';
import { Button, buttonClass, Chip, EmptyState, ErrorBanner, Field, Select } from '../components/ui';
import { Drawer } from '../write/Drawer';
import { TextArea } from '../write/Forms';
import { Link } from '../router';
import { ANSWER_TYPES, KIND_BY_KEY, emptyProtocol, protocolAsText, protocolSize, studyKind, type KindSpec } from './kinds';
import { ProtocolEditor } from './ProtocolEditor';
import './studies.css';

function errorText(e: unknown) {
  return e instanceof ApiRequestError ? e.message : 'L’enregistrement a échoué.';
}

export function StudyProtocolSection({
  projectId,
  study,
  canWrite,
}: {
  projectId: string;
  study: Study;
  canWrite: boolean;
}) {
  const kind = studyKind(study.type);
  const spec = kind ? KIND_BY_KEY[kind] : null;
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!spec) return null;
  const base = `/projet/${encodeURIComponent(projectId)}`;

  if (spec.kind === 'test' || spec.kind === 'audit') {
    const linked = spec.kind === 'test'
      ? study.test_id ? { to: `${base}/tests/${encodeURIComponent(study.test_id)}`, name: study.test_code } : null
      : study.audit_id ? { to: `${base}/audits`, name: study.audit_code } : null;
    return (
      <section className="test-block">
        <div className="block-head">
          <h2 className="t-h3">{spec.protocolName}</h2>
        </div>
        {linked ? (
          <div className="proto-linked">
            <p className="zy-texte-muet">
              {spec.kind === 'test'
                ? 'Le scénario, les questions et les résultats de session vivent sur la fiche du test lié à cette étude.'
                : 'La notation heuristique se fait sur la fiche de l’audit lié à cette étude.'}
            </p>
            <Link to={linked.to} className={buttonClass('ghost')}>
              Ouvrir {spec.kind === 'test' ? 'le test' : 'l’audit'}{linked.name ? ` ${linked.name}` : ''} →
            </Link>
          </div>
        ) : (
          <p className="zy-texte-muet">
            Aucun {spec.kind === 'test' ? 'test' : 'audit'} n’est lié à cette étude (créée avant les assistants par type, ou objet supprimé depuis).
          </p>
        )}
      </section>
    );
  }

  const protocol = study.protocol ?? null;
  const size = protocolSize(protocol);

  async function copy() {
    if (!protocol || !spec) return;
    try {
      await navigator.clipboard.writeText(protocolAsText(spec, protocol, study.objective ?? ''));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      /* presse-papiers refusé : rien à faire */
    }
  }

  return (
    <section className="test-block">
      <div className="block-head">
        <h2 className="t-h3">{spec.protocolName}</h2>
        <div className="proto-actions">
          {protocol && size > 0 ? (
            <Button type="button" variant="ghost" onClick={() => void copy()}>
              {copied ? 'Copié ✓' : 'Copier le texte'}
            </Button>
          ) : null}
          {canWrite && protocol ? (
            <Button type="button" variant="ghost" onClick={() => setEditing(true)}>Modifier</Button>
          ) : null}
        </div>
      </div>

      {protocol ? (
        <ProtocolView spec={spec} protocol={protocol} />
      ) : (
        <EmptyState
          title={`${spec.protocolName} à préparer`}
          description={
            spec.kind === 'entretien'
              ? 'Les thèmes, les questions et leurs relances — à partir d’un modèle, avec l’IA, ou à la main.'
              : spec.kind === 'observation'
                ? 'Les lieux, les moments et les points à observer — à partir d’un modèle, avec l’IA, ou à la main.'
                : 'Les questions et leurs types de réponse — à partir d’un modèle, avec l’IA, ou à la main.'
          }
          action={canWrite ? (
            <Button type="button" variant="accent" onClick={() => setEditing(true)}>
              Préparer {spec.protocolThe}
            </Button>
          ) : undefined}
        />
      )}

      <Drawer
        open={editing}
        wide
        title={spec.protocolName}
        description={study.objective ?? undefined}
        onClose={() => setEditing(false)}
      >
        <ProtocolForm projectId={projectId} study={study} spec={spec} onDone={() => setEditing(false)} />
      </Drawer>
    </section>
  );
}

function ProtocolForm({ projectId, study, spec, onDone }: { projectId: string; study: Study; spec: KindSpec; onDone: () => void }) {
  const [value, setValue] = useState<StudyProtocol>(study.protocol ?? emptyProtocol());
  const update = useUpdateStudy(projectId, study.id);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ protocol: value });
      onDone();
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="write-form" onSubmit={submit} noValidate>
      <ProtocolEditor
        projectId={projectId}
        spec={spec}
        value={value}
        onChange={setValue}
        study={{ objective: study.objective, method: study.method, target: study.target, context: study.context }}
      />
      {update.error ? <ErrorBanner>{errorText(update.error)}</ErrorBanner> : null}
      <Button type="submit" variant="accent" loading={update.isPending}>
        Enregistrer {spec.protocolThe}
      </Button>
    </form>
  );
}

export function ProtocolView({ spec, protocol }: { spec: KindSpec; protocol: StudyProtocol }) {
  let n = 0;
  return (
    <div className="proto-view">
      {protocol.durationMin || protocol.sections.length ? (
        <div className="proto-meta">
          {protocol.durationMin ? <Chip>{protocol.durationMin} min</Chip> : null}
          <Chip>
            {protocolSize(protocol)} {protocolSize(protocol) > 1 ? spec.itemPlural : spec.itemLabel.toLowerCase()}
          </Chip>
          <Chip>
            {protocol.sections.length} {spec.sectionLabel.toLowerCase()}{protocol.sections.length > 1 ? 's' : ''}
          </Chip>
        </div>
      ) : null}
      {protocol.places ? (
        <div className="proto-block"><span className="proto-block-label">Lieux et situations</span>{protocol.places}</div>
      ) : null}
      {protocol.intro ? (
        <div className="proto-block"><span className="proto-block-label">Introduction</span>{protocol.intro}</div>
      ) : null}
      {protocol.sections.map((s, si) => (
        <div key={si} className="proto-view-section">
          <h3>{s.title || `${spec.sectionLabel} ${si + 1}`}</h3>
          <ol className="proto-view-items">
            {s.items.map((it, ii) => {
              n += 1;
              return (
                <li key={ii} className="proto-view-item">
                  <span className="proto-num">{n}</span>
                  <div>
                    <p className="proto-view-text">{it.text}</p>
                    {it.followups ? <p className="proto-view-follow">Relances : {it.followups}</p> : null}
                    {spec.kind === 'questionnaire' ? (
                      <p className="proto-view-answer">
                        <Chip>{ANSWER_TYPES.find((a) => a.value === (it.answerType ?? 'ouverte'))?.label}</Chip>
                        {it.options ? <span>{it.options}</span> : null}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
      {protocol.closing ? (
        <div className="proto-block"><span className="proto-block-label">Conclusion</span>{protocol.closing}</div>
      ) : null}
    </div>
  );
}

// --- Cadrage (modification) -------------------------------------------------

const STATUSES = ['Planifiée', 'En cours', 'Terminée'];

export function StudyFramingForm({ projectId, study, onDone }: { projectId: string; study: Study; onDone: () => void }) {
  const kind = studyKind(study.type);
  const spec = kind ? KIND_BY_KEY[kind] : null;
  const [objective, setObjective] = useState(study.objective ?? '');
  const [method, setMethod] = useState(study.method ?? '');
  const [target, setTarget] = useState(study.target ?? '');
  const [participants, setParticipants] = useState(study.participants == null ? '' : String(study.participants));
  const [period, setPeriod] = useState(study.period ?? '');
  const [status, setStatus] = useState(study.status ?? 'En cours');
  const [context, setContext] = useState(study.context ?? '');
  const update = useUpdateStudy(projectId, study.id);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!objective.trim()) return;
    const n = Number.parseInt(participants, 10);
    try {
      await update.mutateAsync({
        objective: objective.trim(), method, target, period, status, context,
        participants: Number.isFinite(n) ? n : null,
      });
      onDone();
    } catch {
      /* rendu par le bandeau */
    }
  }

  const statuses = STATUSES.includes(status) ? STATUSES : [status, ...STATUSES];
  return (
    <form className="write-form" onSubmit={submit} noValidate>
      <TextArea label="Objectif *" value={objective} onChange={setObjective} rows={3} />
      <Field label="Méthode" value={method} onChange={(e) => setMethod(e.target.value)} list="st-edit-methods" />
      <datalist id="st-edit-methods">
        {(spec?.methods ?? []).map((m) => <option key={m} value={m} />)}
      </datalist>
      <Field label="Cible" value={target} onChange={(e) => setTarget(e.target.value)} />
      <div className="field-row">
        <Field
          label={spec?.participantsLabel || 'Participants'}
          type="number"
          min={0}
          value={participants}
          onChange={(e) => setParticipants(e.target.value)}
        />
        <Field label="Période" value={period} onChange={(e) => setPeriod(e.target.value)} />
        <label className="zy-field">
          <span className="zy-field__label">Statut</span>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </label>
      </div>
      <TextArea label="Contexte" value={context} onChange={setContext} rows={3} />
      {update.error ? <ErrorBanner>{errorText(update.error)}</ErrorBanner> : null}
      <Button type="submit" variant="accent" loading={update.isPending} disabled={!objective.trim()}>
        Enregistrer
      </Button>
    </form>
  );
}
